import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of, throwError, timer } from 'rxjs';
import { map, catchError, tap, finalize, retryWhen, mergeMap, delay } from 'rxjs/operators';
import { AppConfiguration } from 'src/app/app.configuration';
import { EntradasService } from '../../venta/services/entradas.service';
import moment from 'moment';

export interface ValidacionAmigoDatos {
  nombreCompleto: string;
  tipoTarjeta: string;
  tipoColaborador: string;
  namigo: string;
  iCodigoClienteVinculado: string;
  categorias?: any[];
  tarifas?: any[];
}

export interface ValidacionAmigoResponse {
  datosResult: ValidacionAmigoDatos;
  mensajes: any[];
  categorias?: any[];
  tarifas?: any[];
}

export interface AmigoActivo {
  namigo: string;
  iCodigoClienteVinculado: string;
}

@Injectable()
export class AmigosThyssenService {
  private amigoActivo?: AmigoActivo;

  constructor(
    private http: HttpClient,
    private config: AppConfiguration,
    private entradasService: EntradasService
  ) {}

  validarAmigo(numero: string, dni: string): Observable<ValidacionAmigoResponse> {
    // Manejo seguro de conexionId
    let conexionId = '';
    try {
      const conexionIdRaw = localStorage.getItem('conexionId');
      if (conexionIdRaw && conexionIdRaw !== 'undefined' && conexionIdRaw !== 'null') {
        conexionId = JSON.parse(conexionIdRaw);
      }
    } catch (error) {
      conexionId = '';
    }
   
    const params = new HttpParams()
      .set('ConexionId', conexionId)
      .set('namigo', numero)
      .set('id', dni);
  
    const url = `${this.config.getConfig('API_URL_MS')}/api/Externos/ValidarAmigosThyssen`;
  
    // Usar directamente el observable HTTP con reintento automático para errores HTTP 0
    const observable = this.http.get<ValidacionAmigoResponse>(url, { params })
      .pipe(
        retryWhen(errors => {
          let retryCount = 0;
          const maxRetries = 3; // Reintentar hasta 3 veces
          
          return errors.pipe(
            mergeMap((error) => {
              // Solo reintentar si es un error HTTP 0
              if (error.status === 0 || error.status === undefined) {
                retryCount++;
                if (retryCount <= maxRetries) {
                  // Aumentar el delay progresivamente: 1s, 2s, 3s
                  const delayMs = retryCount * 1000;
                  // Esperar con delay progresivo antes de reintentar
                  return timer(delayMs);
                }
              }
              // Si no es HTTP 0 o ya se agotaron los reintentos, lanzar el error
              return throwError(error);
            })
          );
        }),
        catchError((error) => {
          throw error;
        })
      );
    
    return observable;
  }
  

  setAmigoActivo(amigo?: AmigoActivo): void {
    if (!amigo) {
      this.amigoActivo = undefined;
      return;
    }
    this.amigoActivo = amigo;
  }

  getAmigoActivo(): AmigoActivo | undefined {
    return this.amigoActivo;
  }

  clearAmigoActivo(): void {
    this.amigoActivo = undefined;
  }

  /**
   * Formatea la fecha al formato DD/MM/YYYY que espera la API
   * @param fecha - Puede ser un objeto Moment, string en formato YYYY/MM/DD, o Date
   * @returns String en formato DD/MM/YYYY o '' si no se proporciona fecha
   */
  private formatearFechaParaAPI(fecha: any): string {
    if (!fecha) {
      return '';
    }

    try {
      // Si es un objeto Moment
      if (typeof fecha === 'object' && fecha.format) {
        return fecha.format('DD/MM/YYYY');
      }

      // Si es un string
      if (typeof fecha === 'string') {
        // Si viene en formato YYYY/MM/DD, convertir a DD/MM/YYYY
        if (fecha.includes('/')) {
          const partes = fecha.split('/');
          if (partes.length === 3 && partes[0].length === 4) {
            // Formato YYYY/MM/DD -> DD/MM/YYYY
            return `${partes[2]}/${partes[1]}/${partes[0]}`;
          } else {
            // Asumir que ya está en formato DD/MM/YYYY
            return fecha;
          }
        }
        
        // Si es otro formato, intentar parsearlo con moment
        return moment(fecha).format('DD/MM/YYYY');
      }

      // Si es un objeto Date
      if (fecha instanceof Date) {
        const dia = String(fecha.getDate()).padStart(2, '0');
        const mes = String(fecha.getMonth() + 1).padStart(2, '0');
        const año = fecha.getFullYear();
        return `${dia}/${mes}/${año}`;
      }

      return '';
    } catch (error) {
      return '';
    }
  }

  cargarCategoriasYTarifas(codigoCliente: string, fecha?: any, categoriaId?: string): Observable<{categorias: any[], tarifas: any[]}> {
    return new Observable(observer => {
      // Cargar categorías con los parámetros específicos de la US
      this.getCategoriasAmigosThyssen(codigoCliente, fecha).subscribe(categorias => {
        // Cargar tarifas con los parámetros específicos de la US
        this.getTarifasAmigosThyssen(codigoCliente, fecha, categoriaId).subscribe(tarifas => {
          observer.next({ categorias, tarifas });
          observer.complete();
        }, error => {
          observer.error(error);
        });
      }, error => {
        observer.error(error);
      });
    });
  }

  // Método para cargar solo las tarifas de una categoría específica
  cargarTarifasPorCategoria(codigoCliente: string, categoriaId: string, fecha?: any): Observable<any[]> {
    return this.getTarifasAmigosThyssen(codigoCliente, fecha, categoriaId);
  }

  // Método específico para GetKoreCategorias con parámetros de Amigos Thyssen
  private getCategoriasAmigosThyssen(codigoCliente: string, fecha?: any): Observable<any[]> {
    // Manejo seguro de conexionId
    let conexionId = '';
    try {
      const conexionIdRaw = localStorage.getItem('conexionId');
      if (conexionIdRaw && conexionIdRaw !== 'undefined' && conexionIdRaw !== 'null') {
        conexionId = JSON.parse(conexionIdRaw);
      }
    } catch (error) {
      conexionId = '';
    }
    
    // Fecha NO se incluye según la especificación de la US
    // Para Amigos Thyssen, GetKoreCategorias solo requiere:
    // CodigoCliente, FiltrarPorCliente: "1", TPVId: "0"
    
    const body = {
      ConexionIacpos: conexionId,
      TPVId: '0', // Según la US
      CodigoCliente: codigoCliente,
      FiltrarPorCliente: '1' // Según la US
    };

    return this.http.post(`${this.config.getConfig('API_URL')}api/GetKoreCategorias`, body)
      .pipe(
        map((res: any) => {
          if (!res.DatosResult) {
            return [];
          }
          return res.DatosResult.ListadoCategoriasDisponibles;
        })
      );
  }

  // Método específico para GetKoreTarifasDisponibles con parámetros de Amigos Thyssen
  private getTarifasAmigosThyssen(codigoCliente: string, fecha?: any, categoriaId?: string): Observable<any[]> {
    
    // Manejo seguro de conexionId
    let conexionId = '';
    try {
      const conexionIdRaw = localStorage.getItem('conexionId');
      if (conexionIdRaw && conexionIdRaw !== 'undefined' && conexionIdRaw !== 'null') {
        conexionId = JSON.parse(conexionIdRaw);
      }
    } catch (error) {
      conexionId = '';
    }
    const categoriaIdFinal = categoriaId ? parseInt(categoriaId) : 0;
    
    // Fecha NO se incluye según la especificación de la US
    // Para Amigos Thyssen, GetKoreTarifasDisponibles solo requiere:
    // CodigoCliente, FiltrarPorCliente: "1", TPVId: "0", CategoriaId
    
    const body = {
      ConexionIacpos: conexionId,
      TPVId: '0', // Según la US
      CodigoCliente: codigoCliente,
      FiltrarPorCliente: '1', // Según la US
      CategoriaId: categoriaIdFinal // Usar la categoría seleccionada o 0 para todas
    };

    return this.http.post(`${this.config.getConfig('API_URL')}api/GetKoreTarifasDisponibles`, body)
      .pipe(
        map((res: any) => {
          if (!res.DatosResult) {
            return [];
          }
          return res.DatosResult.ListadoKoreTarifasDisponibles;
        }),
        catchError((error) => {
          return of([]);
        })
      );
  }
}
